import classNames from "classnames";
import DOMPurify from "dompurify";
import moment from "moment/moment";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { Controller, useForm } from "react-hook-form";
import { HiEllipsisVertical } from "react-icons/hi2";
import { MdBolt } from "react-icons/md";
import { RxDragHandleDots2 } from "react-icons/rx";
import { toast } from "react-toastify";

import { AvatarPlaceholder } from "../../../../../../components/avatar/AvatarPlaceholder";
import {
  AvatarStack,
  AvatarStackItem,
} from "../../../../../../components/avatar/AvatarStack";
import Button from "../../../../../../components/button/Button";
import { StyledDatePicker } from "../../../../../../components/datepicker/datepicker";
import { Dropdown } from "../../../../../../components/dropdown/new-dropdown/Dropdown";
import StatusMenu from "../../../../../../components/dropdown/StatusMenu";
import { SettingsConfirmModal } from "../../../../../../components/modal/SettingsConfirmModal";
import { TrashSvg } from "../../../../../../components/svg/TrashSvg";
import { getInitials } from "../../../../../../constants/constants";
import { WorkDetailsModel } from "../../../../../../factories/work/model/work-details.model";
import {
  toUpdateWorkTaskModel,
  WorkTaskModel,
} from "../../../../../../factories/work/model/work-task.model";
import { useAppSelector } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { useAddJobTaskAutomationMutation } from "../../../../../../slices/AutomationSlice";
import {
  useDeleteWorkTaskMutation,
  useUpdateWorkTaskAssigneesMutation,
  useUpdateWorkTaskMutation,
  useUpdateWorkTaskStatusMutation,
} from "../../../../../../slices/WorkTasksSlice";
import { useGetWorkTypeQuery } from "../../../../../../slices/WorkTypeSlice";
import { IUser } from "../../../../../../types/types";
import { serverMessage } from "../../../../../../utils/serverMessage";
import { WorkTaskModal } from "../../../../modals/WorkTaskModal";
import { subtaskDueProblem } from "../../../../subtaskDue.utils";
import { AddAutomationModal } from "../../../automation/AddAutomationModal";
import { AutomationDraft } from "../../../automation/automation.constants";
import {
  getAutomationActionBodyValue,
  getAutomationConditionBodyValue,
} from "../../../automation/automation.helpers";
import { AutomationSummaryPart } from "../../../automation/automation.summary";
import { AutomationTarget } from "../../../automation/automation.types";
import { AutomationRuleTooltip } from "../../../automation/AutomationRuleTooltip";
import { useAutomationLookups } from "../../../automation/useAutomationLookups";
import { WorkTaskTimer } from "./timer/WorkTaskTimer";

type Props = {
  workTask: WorkTaskModel;
  users: IUser[];
  work: WorkDetailsModel;
  index: number;
  length: number;
  provided: DraggableProvided;
  automationRule?: AutomationSummaryPart[];
  onStatusChangeSuccess?: (taskId: string, isDone: boolean) => Promise<void>;
};

type FormData = {
  name: string;
  description: string;
  dueDate: string;
  startDate: string;
};

const MAX_ASSIGNEES = 5;

const getTaskAssigneeIds = (workTask: WorkTaskModel): string[] => {
  if (workTask.jobTaskAssignees?.length) {
    return workTask.jobTaskAssignees.map((assignee) => assignee.assignedUserId);
  }

  return [];
};

const getTaskAssigneeItems = (workTask: WorkTaskModel): AvatarStackItem[] => {
  if (workTask.jobTaskAssignees?.length) {
    return workTask.jobTaskAssignees.map((assignee) => ({
      id: assignee.assignedUserId,
      label: getInitials(
        assignee.assignedUser.userProfile.firstName,
        assignee.assignedUser.userProfile.lastName,
      ),
      name: `${assignee.assignedUser.userProfile.firstName || ""} ${
        assignee.assignedUser.userProfile.lastName || ""
      }`.trim(),
      color: assignee.assignedUser.userProfile.color,
    }));
  }

  return workTask.assignedUser
    ? [
        {
          id: workTask.assignedUser.id,
          label: getInitials(
            workTask.assignedUser.userProfile.firstName,
            workTask.assignedUser.userProfile.lastName,
          ),
          name: `${workTask.assignedUser.userProfile.firstName || ""} ${
            workTask.assignedUser.userProfile.lastName || ""
          }`.trim(),
          color: workTask.assignedUser.userProfile.color,
        },
      ]
    : [];
};

const areStringArraysEqual = (first: string[], second: string[]): boolean => {
  if (first.length !== second.length) {
    return false;
  }

  const firstSorted = [...first].sort();
  const secondSorted = [...second].sort();

  return firstSorted.every((item, index) => item === secondSorted[index]);
};

export const WorkTask: FC<Props> = ({
  workTask,
  users,
  work,
  length,
  index,
  provided,
  automationRule,
  onStatusChangeSuccess,
}) => {
  const { user } = useAppSelector((state) => state.appReducer);

  const automationLookups = useAutomationLookups(work.serviceId);

  const [addJobTaskAutomation, { isLoading: isAddingAutomation }] =
    useAddJobTaskAutomationMutation();
  const isAddingAutomationRef = useRef(false);
  const [updateWorkTaskAssignees, { isLoading: isUpdatingAssignees }] =
    useUpdateWorkTaskAssigneesMutation();
  const [updateWorkTaskStatus] = useUpdateWorkTaskStatusMutation();
  const [deleteWorkTask, { isLoading: isDeletingWorkTask }] =
    useDeleteWorkTaskMutation();
  const [updateWorkTask] = useUpdateWorkTaskMutation();

  const { data } = useGetWorkTypeQuery({ id: work.serviceId });

  const completedStatus = useMemo(
    () =>
      [...(data?.serviceStatuses ?? [])].find(
        (item) => item.name === "Completed",
      ),
    [data?.serviceStatuses],
  );

  const [previewIsDone, setPreviewIsDone] = useState<boolean | null>(null);
  // SUB-014: while this subtask's own save is running, list refreshes started by other
  // subtasks may still carry its old tick; keep showing the new tick until the save ends.
  const [savingTick, setSavingTick] = useState(0);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [isOpenAutomation, setIsOpenAutomation] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [draftAssigneeIds, setDraftAssigneeIds] = useState<string[]>(() =>
    getTaskAssigneeIds(workTask),
  );

  const shownIsDone = previewIsDone ?? workTask.isDone;
  const taskAssigneeIds = useMemo(
    () => getTaskAssigneeIds(workTask),
    [workTask],
  );
  const assigneeItems = useMemo(
    () => getTaskAssigneeItems(workTask),
    [workTask],
  );
  const isDraftAssigneesChanged = !areStringArraysEqual(
    draftAssigneeIds,
    taskAssigneeIds,
  );

  const createdBy = useMemo(() => {
    const fullName = `${user?.userProfile?.firstName || ""} ${
      user?.userProfile?.lastName || ""
    }`.trim();

    return fullName || "Unknown user";
  }, [user?.userProfile?.firstName, user?.userProfile?.lastName]);

  const automationTarget = useMemo<AutomationTarget>(() => {
    return {
      type: "subtask",
      id: workTask.id,
      title: workTask.name,
      work,
      workTask,
    };
  }, [work, workTask]);

  const { control, setValue } = useForm<FormData>({
    defaultValues: {
      dueDate: moment(workTask?.dueDate).isValid()
        ? moment(workTask?.dueDate).toISOString()
        : moment().toISOString(),
    },
  });

  const today = new Date();
  const dateToCompare = new Date(workTask.dueDate);
  const automationTooltipId = `work-task-automation-${workTask.id}`;

  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const compareDate = new Date(
    dateToCompare.getFullYear(),
    dateToCompare.getMonth(),
    dateToCompare.getDate(),
  );

  const handleChangeWorkTaskStatus = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextIsDone = e.target.checked;

    setPreviewIsDone(nextIsDone);
    setSavingTick((count) => count + 1);

    try {
      await updateWorkTaskStatus({
        taskId: workTask.id,
        isDone: nextIsDone,
        updatedByUserId: user.id,
      }).unwrap();

      if (completedStatus?.id) {
        await updateWorkTask(
          toUpdateWorkTaskModel(workTask, {
            statusId: completedStatus.id,
            subStatusId: null,
          }),
        );
      } else {
        toast("Something went wrong while updating status", {
          type: "error",
          pauseOnHover: false,
        });
      }

      await onStatusChangeSuccess?.(workTask.id, nextIsDone);
    } catch {
      setPreviewIsDone(null);

      toast("Something went wrong", {
        type: "error",
        pauseOnHover: false,
      });
    } finally {
      setSavingTick((count) => count - 1);
    }
  };

  const resetDraftAssignees = () => {
    setDraftAssigneeIds(taskAssigneeIds);
  };

  const onToggleDraftAssignee = (assigneeId: string) => {
    setDraftAssigneeIds((currentIds) => {
      if (currentIds.includes(assigneeId)) {
        return currentIds.filter((item) => item !== assigneeId);
      }

      if (currentIds.length >= MAX_ASSIGNEES) {
        return currentIds;
      }

      return [...currentIds, assigneeId];
    });
  };

  const onSaveAssignees = async (closeDropdown: () => void) => {
    if (
      draftAssigneeIds.length === 0 ||
      draftAssigneeIds.length > MAX_ASSIGNEES ||
      !isDraftAssigneesChanged ||
      isUpdatingAssignees
    ) {
      return;
    }

    try {
      await updateWorkTaskAssignees({
        jobTaskId: workTask.id,
        userIds: draftAssigneeIds,
      }).unwrap();

      toast.success("Subtask assignees updated successfully");
      closeDropdown();
    } catch {
      toast.error("Failed to update subtask assignees");
    }
  };

  const handleSaveAutomation = async (
    draft: AutomationDraft,
    target: AutomationTarget,
  ) => {
    if (isAddingAutomationRef.current) {
      return;
    }

    if (!draft.conditionId || !draft.actionId) {
      return;
    }

    const orgId = work.orgId || getLinkedFirm()?.orgId || "";

    if (!orgId || !user?.id) {
      toast.error("Missing organization or user for automation");
      return;
    }

    const conditionValue = getAutomationConditionBodyValue({
      conditionKey: draft.conditionKey,
      conditionValue: draft.conditionValue,
      conditionExtraValue: draft.conditionExtraValue,
      workStatusOptions: automationLookups.options.workStatuses,
      subtaskStatusOptions: automationLookups.options.subtaskStatuses,
    });
    const actionValue = getAutomationActionBodyValue({
      actionKey: draft.actionKey,
      actionValue: draft.actionValue,
      actionExtraValue: draft.actionExtraValue,
      workStatusOptions: automationLookups.options.workStatuses,
      subtaskStatusOptions: automationLookups.options.subtaskStatuses,
    });

    isAddingAutomationRef.current = true;

    try {
      await addJobTaskAutomation({
        orgId,
        jobTaskId: target.id,
        createdById: user.id,
        conditionId: draft.conditionId,
        actionId: draft.actionId,
        isActive: true,
        isExecuted: false,
        isSuccess: false,
        conditionValue,
        actionValue,
      }).unwrap();

      toast.success("Subtask automation created successfully");
      setIsOpenAutomation(false);
    } catch {
      toast.error("Failed to create subtask automation");
    } finally {
      isAddingAutomationRef.current = false;
    }
  };

  const handleConfirmDeleteWorkTask = async () => {
    try {
      await deleteWorkTask(workTask).unwrap();
      setIsDeleteConfirmOpen(false);
    } catch {
      toast("Something went wrong while deleting subtask", {
        type: "error",
        pauseOnHover: false,
      });
    }
  };

  useEffect(() => {
    setValue(
      "dueDate",
      moment(workTask?.dueDate).isValid()
        ? moment(workTask?.dueDate).toISOString()
        : moment().toISOString(),
    );
  }, [setValue, workTask]);

  useEffect(() => {
    if (
      savingTick === 0 &&
      previewIsDone !== null &&
      workTask.isDone === previewIsDone
    ) {
      setPreviewIsDone(null);
    }
  }, [previewIsDone, savingTick, workTask.isDone]);

  useEffect(() => {
    setDraftAssigneeIds(taskAssigneeIds);
  }, [taskAssigneeIds]);

  return (
    <>
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={provided.draggableProps.style}
        className={
          "grid w-full grid-cols-[2fr_2fr_59fr_2fr] items-center border-t border-gray-200 bg-base-100 py-2 pr-2"
        }>
        <RxDragHandleDots2 size={24} className={"mr-2 text-gray-200"} />

        <input
          type={"checkbox"}
          className={
            "checkbox mr-4 scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
          }
          checked={shownIsDone}
          onChange={handleChangeWorkTaskStatus}
        />

        <div className={classNames(" text-sm", shownIsDone && "line-through")}>
          <div>
            <div className={"flex w-full justify-between"}>
              <div className={"relative min-w-0 font-bold"}>
                {automationRule?.length ? (
                  <>
                    <MdBolt
                      className={
                        "absolute -left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-yellow-300"
                      }
                      data-tooltip-id={automationTooltipId}
                    />
                    <AutomationRuleTooltip
                      id={automationTooltipId}
                      parts={automationRule}
                    />
                  </>
                ) : null}
                <span className={"truncate font-medium"}>{workTask?.name}</span>
              </div>
            </div>

            <div
              className={"rich-text-content text-gray-500"}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(workTask?.description || ""),
              }}
            />
          </div>

          <div className={"mt-4 flex justify-between"}>
            <div className={"flex items-center gap-4"}>
              <div className={"h-6 cursor-pointer"}>
                <div>
                  <Dropdown
                    triggerElement={
                      <Button
                        label={""}
                        colorType={"ghost"}
                        size={"custom"}
                        icon={
                          <AvatarStack
                            items={assigneeItems}
                            withPreview
                            previewTitle={"Assignees:"}
                          />
                        }
                        extraClasses={
                          "text-purple border btn-xs p-0 border-none hover:bg-transparent"
                        }
                      />
                    }>
                    {({ close }) => (
                      <div
                        className={
                          "flex max-h-[300px] w-[245px] flex-col rounded-[5px] border border-gray-300 bg-white text-sm shadow-box"
                        }>
                        <div
                          className={
                            "link-primary border-b-[1px] border-b-primary px-4 py-2 text-sm"
                          }>
                          Reassign subtask to:
                        </div>
                        <div className={"px-4 pt-2 text-xs text-[#8B98A8]"}>
                          {draftAssigneeIds.length} / {MAX_ASSIGNEES} selected
                        </div>
                        <div className={"max-h-[200px] overflow-y-auto"}>
                          {users?.map((item) => {
                            const isSelected = draftAssigneeIds.includes(
                              item.id,
                            );
                            const isDisabled =
                              !isSelected &&
                              draftAssigneeIds.length >= MAX_ASSIGNEES;

                            return (
                              <label
                                key={item?.id}
                                className={
                                  "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[:disabled]:hover:bg-white has-[:disabled]:hover:text-inherit flex cursor-pointer items-center gap-1 px-4 py-2 hover:bg-blue-400 hover:text-white"
                                }>
                                <input
                                  type={"checkbox"}
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() =>
                                    onToggleDraftAssignee(item.id)
                                  }
                                  className={
                                    "checkbox mr-1 scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
                                  }
                                />
                                <AvatarPlaceholder
                                  size={"2xs"}
                                  type={"custom"}
                                  avatarCustomColor={item?.userProfile?.color}
                                  label={getInitials(
                                    item?.userProfile?.firstName || "",
                                    item?.userProfile?.lastName || "",
                                  )}
                                />

                                <div
                                  className={
                                    "whitespace-pre-wrap"
                                  }>{`${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`}</div>
                              </label>
                            );
                          })}
                        </div>
                        <div
                          className={
                            "grid grid-cols-2 gap-2 border-t border-gray-200 p-2"
                          }>
                          <Button
                            fullWidth
                            label={"Cancel"}
                            size={"custom"}
                            colorType={"outline"}
                            extraClasses={"btn-xs"}
                            buttonType={"button"}
                            onClick={() => {
                              resetDraftAssignees();
                              close();
                            }}
                          />
                          <Button
                            fullWidth
                            label={"Save"}
                            size={"custom"}
                            extraClasses={"btn-xs"}
                            buttonType={"button"}
                            onClick={() => onSaveAssignees(close)}
                            isLoading={isUpdatingAssignees}
                            disabled={
                              draftAssigneeIds.length === 0 ||
                              draftAssigneeIds.length > MAX_ASSIGNEES ||
                              !isDraftAssigneesChanged ||
                              isUpdatingAssignees
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Dropdown>
                </div>
              </div>

              <div className={"flex min-w-fit items-center gap-2"}>
                <div className={"flex justify-end"}>
                  <StatusMenu
                    workTask={workTask}
                    workTypeId={work.serviceId}
                    index={index}
                    length={length}
                    variant={"small"}
                  />
                </div>
              </div>
            </div>

            <div
              className={
                "flex items-center justify-end gap-4 text-end text-gray-800"
              }>
              <WorkTaskTimer work={work} workTask={workTask} />

              <div className={"flex w-fit items-center"}>
                <div
                  className={classNames(
                    "text-xs font-semibold",
                    compareDate < todayDate
                      ? "text-orange-300"
                      : "text-[#9FA1AD]",
                  )}>
                  Due:
                </div>

                <Controller
                  name={"dueDate"}
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    const handleDateChange = async (date: Date | null) => {
                      if (date instanceof Date && !isNaN(date.getTime())) {
                        const isoDate = moment(date).toISOString();
                        const currentIsoDate = moment(
                          workTask.dueDate,
                        ).isValid()
                          ? moment(workTask.dueDate).toISOString()
                          : "";

                        if (isoDate === currentIsoDate) {
                          return;
                        }

                        // Owner 2026-10-22: never after the work's due date.
                        const dueProblem = subtaskDueProblem(
                          isoDate,
                          work.dueDate,
                        );
                        if (dueProblem) {
                          toast.error(dueProblem);
                          return;
                        }

                        onChange(isoDate);

                        try {
                          await updateWorkTask(
                            toUpdateWorkTaskModel(workTask, {
                              dueDate: isoDate,
                            }),
                          ).unwrap();
                        } catch (error) {
                          onChange(currentIsoDate || moment().toISOString());
                          toast.error(
                            serverMessage(
                              error,
                              "Failed to update subtask due date",
                            ),
                          );
                        }
                      }
                    };

                    return (
                      <StyledDatePicker
                        value={value ? moment(value).toDate() : new Date()}
                        onChange={handleDateChange}
                        maxDate={moment(work.dueDate).toDate()}
                        onOutOfRange={(date) =>
                          toast.error(
                            subtaskDueProblem(date, work.dueDate) ||
                              "That date isn't allowed for this subtask.",
                            { toastId: "subtask-due-after-work" },
                          )
                        }
                        styleType={"ghost"}
                        fontWeight={"500"}
                        fontSize={"12px"}
                        width={"85px"}
                        color={
                          compareDate < todayDate
                            ? "rgb(253 186 116)"
                            : "#9FA1AD"
                        }
                        textAlign={"end"}
                        format={"MMM dd, yyyy"}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <Dropdown
            triggerElement={
              <Button
                label={""}
                colorType={"ghost"}
                size={"custom"}
                icon={<HiEllipsisVertical size={20} />}
                extraClasses={"btn-xs p-0.5"}
              />
            }>
            <div
              className={
                "rounded-[5px] border border-gray-300 bg-white text-sm shadow-box"
              }>
              <div
                onClick={() => setIsOpenUpdate(true)}
                className={
                  "cursor-pointer rounded-[5px] px-[15px] py-[5px] text-center hover:bg-gray-300"
                }>
                Edit
              </div>

              <div
                onClick={() => setIsOpenAutomation(true)}
                className={
                  "cursor-pointer rounded-[5px] px-[15px] py-[5px] text-center hover:bg-gray-300"
                }>
                Automation
              </div>

              <div
                onClick={() => setIsDeleteConfirmOpen(true)}
                className={
                  "cursor-pointer rounded-[5px] px-[15px] py-[5px] text-center hover:bg-gray-300"
                }>
                Delete
              </div>
            </div>
          </Dropdown>
        </div>
      </div>

      {isOpenUpdate && (
        <WorkTaskModal
          isActive={isOpenUpdate}
          onClose={() => setIsOpenUpdate(false)}
          defaultValues={workTask}
          work={work}
          users={users}
        />
      )}

      {isOpenAutomation && (
        <AddAutomationModal
          isOpen={isOpenAutomation}
          target={automationTarget}
          createdBy={createdBy}
          lookups={automationLookups}
          isSaving={isAddingAutomation}
          onClose={() => setIsOpenAutomation(false)}
          onSave={handleSaveAutomation}
        />
      )}

      {isDeleteConfirmOpen && (
        <SettingsConfirmModal
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={handleConfirmDeleteWorkTask}
          isLoading={isDeletingWorkTask}
          icon={<TrashSvg className={"h-5 w-5"} />}
          title={`Delete '${workTask.name}' subtask?`}
          description={"Do you want to permanently delete this subtask?"}
          confirmText={"Delete"}
          variant={"danger"}
        />
      )}
    </>
  );
};
